import * as React from "react";
import { useContext } from "react";

import { getNextPayoutDate, PayoutInterval, StripeCapabilityStatus, OrganizationCountry } from "@simplyk/common";
import { inferRouterOutputs } from "@trpc/server";

import { trpc, AppRouterType } from "../helpers/trpc";

import { useCurrentUserContext } from "./CurrentUserContext";

import { IssuingCardOutput } from "@/types/trpc";

type StripeAccountOutput = inferRouterOutputs<AppRouterType>["getStripeCustomAccount"];

export interface StripeAccountContextProps {
  stripeAccountHasRequirements: boolean;
  transferIsActive: boolean;
  transferIsPending: boolean;
  getStripeCustomAccountLoading: boolean;
  requirementDelay?: string;
  stripeCustomBalanceLoading: boolean;
  hasStripeCustomFlowStarted: boolean;
  isStripeCustomAccountActive: boolean;
  isStripeCustomBankConnected?: boolean;
  refetchOrganization: () => void;
  pendingCustomBalance: number;
  availableCustomBalance: number;
  stripeCustomBalance: number;
  refetchStripeAccount: () => void;
  isBankRequirementDue?: boolean;
  futureRequirements: string[];
  stripeCustomAccount?: StripeAccountOutput | null;
  hasAtLeastOnePayout?: boolean | null;
  hasOnlyOnePayout?: boolean | null;
  stripeCustomAccountId?: string | null;
  issuingBalance: number;
  issuingPendingBalance: number;
  nextPayoutDate?: Date;
  refetchBalance: () => void;
  stripeIssuingCard: IssuingCardOutput | null | undefined;
  refetchIssuingCard: () => void;
  isStripeIssuingCapabilityActive: boolean;
  requirementsCurrentlyDue?: string[];
  isStripePendingVerification: boolean;
  financialAccount: { hasFinancialAccount: boolean; isLoading: boolean };
  payoutsDisabledOnStripe: boolean;
  shouldRevealPayoutsPaused: "Yes" | "No" | "Unset";
}

export const defaultValue: StripeAccountContextProps = {
  stripeAccountHasRequirements: true,
  transferIsActive: true,
  transferIsPending: false,
  getStripeCustomAccountLoading: true,
  requirementDelay: "",
  stripeCustomBalanceLoading: true,
  hasStripeCustomFlowStarted: false,
  isStripeCustomAccountActive: false,
  refetchOrganization: () => {},
  pendingCustomBalance: 0,
  availableCustomBalance: 0,
  stripeCustomBalance: 0,
  refetchStripeAccount: () => {},
  isStripeCustomBankConnected: true,
  isBankRequirementDue: false,
  futureRequirements: [],
  stripeCustomAccount: null,
  hasAtLeastOnePayout: null,
  hasOnlyOnePayout: null,
  stripeCustomAccountId: null,
  issuingBalance: 0,
  issuingPendingBalance: 0,
  nextPayoutDate: undefined,
  refetchBalance: () => {},
  stripeIssuingCard: null,
  refetchIssuingCard: () => {},
  isStripeIssuingCapabilityActive: false,
  isStripePendingVerification: false,
  financialAccount: { hasFinancialAccount: false, isLoading: true },
  payoutsDisabledOnStripe: false,
  shouldRevealPayoutsPaused: "Unset",
};

export const StripeAccountContext = React.createContext<StripeAccountContextProps>(defaultValue);

export const useStripeAccountContext = (): StripeAccountContextProps => {
  return useContext(StripeAccountContext);
};

export const StripeAccountProvider: React.FunctionComponent<React.PropsWithChildren> = ({ children }) => {
  const {
    isStripeCustomAccountActive,
    hasStripeCustomFlowStarted,
    refetchOrganization,
    currentUser,
    isOrganization,
    organization,
  } = useCurrentUserContext();
  const skip = !currentUser || !isOrganization;
  const {
    data: getStripeCustomAccount,
    isLoading: getStripeCustomAccountLoading,
    refetch: refetchStripeAccount,
  } = trpc.getStripeCustomAccount.useQuery(undefined, { enabled: !skip });
  const {
    data: getStripeCustomBalance,
    isLoading: stripeCustomBalanceLoading,
    refetch: refetchBalance,
  } = trpc.getStripeAccountBalance.useQuery(undefined, {
    enabled: !skip,
  });

  const { data: getIssuingCard, refetch: refetchIssuingCard } = trpc.getTreasuryIssuingCard.useQuery(undefined, {
    enabled: !skip && Boolean(organization?.featureIssuingEnabled),
  });
  const stripeIssuingCard = getIssuingCard?.issuingCard;

  const { data: treasuryFinancialAccount } = trpc.getTreasuryFinancialAccount.useQuery(undefined, {
    enabled: !skip && Boolean(organization?.featureTreasuryEnabled),
  });

  const issuingBalance = getStripeCustomBalance?.issuing?.available?.[0]?.amount || 0;
  const issuingPendingBalance = getStripeCustomBalance?.issuing?.pending?.[0]?.amount || 0;

  const pendingCustomBalance = getStripeCustomBalance?.pending?.[0]?.amount || 0;
  const availableCustomBalance = getStripeCustomBalance?.available?.[0]?.amount || 0;
  const stripeCustomBalance = availableCustomBalance;

  // TODO on filter out le future requirement "external_account": to see with product a better handling
  const futureRequirements =
    getStripeCustomAccount?.future_requirements?.currently_due?.filter(
      (requirement: string) => requirement !== "external_account"
    ) || [];

  const requirementsCurrentlyDue =
    getStripeCustomAccount?.requirements?.currently_due?.filter((requirement) => requirement !== "external_account") ||
    [];

  const stripeAccountHasRequirements = Boolean(requirementsCurrentlyDue.length > 0 || futureRequirements?.length > 0);

  const isBankRequirementDue = getStripeCustomAccount?.requirements?.currently_due?.includes("external_account");
  const requirementsDeadline = getStripeCustomAccount?.requirements?.current_deadline;
  const requirementDelay = requirementsDeadline
    ? Math.ceil((requirementsDeadline - Date.now()) / (1000 * 60 * 60 * 24)).toString()
    : undefined;
  // Stripe pending verification only for Canadian organizations
  const isStripePendingVerification =
    Boolean(getStripeCustomAccount?.requirements?.pending_verification?.length) &&
    organization?.country === OrganizationCountry.Canada;
  const transferIsActive = getStripeCustomAccount?.capabilities?.transfers === StripeCapabilityStatus.Active;
  const transferIsPending = getStripeCustomAccount?.capabilities?.transfers === StripeCapabilityStatus.Pending;
  const stripeCustomAccountId = getStripeCustomAccount?.id;
  const isStripeIssuingCapabilityActive = Boolean(
    getStripeCustomAccount?.capabilities?.card_issuing === StripeCapabilityStatus.Active
  );

  const isStripeCustomBankConnected = getStripeCustomAccount?.isBankConnected;

  const stripeCustomAccount = getStripeCustomAccount;
  const lastPayoutCreatedAtUtc = React.useMemo(
    () =>
      stripeCustomAccount?.lastPayout?.createdAt ? new Date(stripeCustomAccount?.lastPayout?.createdAt) : undefined,
    [stripeCustomAccount?.lastPayout?.createdAt]
  );
  const nextPayoutDate = React.useMemo(() => {
    if (organization?.featureIssuingEnabled && !organization?.featureTreasuryEnabled) {
      return getNextPayoutDate(new Date(), PayoutInterval.Weekly, lastPayoutCreatedAtUtc);
    }
    return (
      stripeCustomAccount?.payoutInterval &&
      getNextPayoutDate(new Date(), stripeCustomAccount?.payoutInterval, lastPayoutCreatedAtUtc)
    );
  }, [
    organization?.featureIssuingEnabled,
    organization?.featureTreasuryEnabled,
    stripeCustomAccount?.payoutInterval,
    lastPayoutCreatedAtUtc,
  ]);

  const shouldRevealPayoutsPaused = (() => {
    switch (organization?.hubspotDisplayPayoutsPaused) {
      case true:
        return "Yes";
      case false:
        return "No";
      default:
        return "Unset";
    }
  })();

  return (
    <StripeAccountContext.Provider
      value={{
        financialAccount: {
          hasFinancialAccount: Boolean(treasuryFinancialAccount),
          isLoading: !organization,
        },
        stripeAccountHasRequirements,
        transferIsActive,
        transferIsPending,
        getStripeCustomAccountLoading,
        issuingBalance,
        issuingPendingBalance,
        requirementDelay,
        stripeCustomBalanceLoading,
        hasStripeCustomFlowStarted,
        isStripeCustomAccountActive,
        refetchOrganization,
        pendingCustomBalance,
        availableCustomBalance,
        stripeCustomBalance,
        stripeCustomAccount,
        refetchStripeAccount,
        isStripeCustomBankConnected,
        isBankRequirementDue,
        futureRequirements,
        hasAtLeastOnePayout: getStripeCustomAccount?.hasAtLeastOnePayout,
        hasOnlyOnePayout: getStripeCustomAccount?.hasOnlyOnePayout,
        stripeCustomAccountId,
        nextPayoutDate,
        refetchBalance,
        stripeIssuingCard,
        refetchIssuingCard,
        isStripeIssuingCapabilityActive,
        requirementsCurrentlyDue,
        isStripePendingVerification,
        payoutsDisabledOnStripe: Boolean(organization?.payoutsDisabledOnStripe),
        shouldRevealPayoutsPaused,
      }}
    >
      {children}
    </StripeAccountContext.Provider>
  );
};
